import { FC } from 'react';

import { CountryEntity, KycEntity } from 'api/types/entity';
import { DialogConfirmRevokeOtpVerification } from 'components';
import { parseFullName } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { ClientFormContent } from 'modules/client/common/components';
import { ActionProps } from 'modules/client/common/components/ClientFormContent/types';
import {
  LockAndUnlockReadOnlyProps,
  useLockAndUnlockReadOnly,
} from 'modules/client/common/hooks';

import { useFormContext } from '../../../forms/representativeForm';
import { RepresentativeClientMainInfoType } from '../../../types';
import { RepresentativeSections } from '../../RepresentativeSections';

export interface FormContentProps extends LockAndUnlockReadOnlyProps {
  countriesLoading?: boolean;
  countries: CountryEntity[];
  kycData?: KycEntity;
  submitLoading?: boolean;
  revokeOtpVerificationLoading?: boolean;
  mainInfo?: RepresentativeClientMainInfoType;
  onShowVersionHistory?: () => void;
  onRevokeOtpVerification?: () => Promise<any>;
}

export const FormContent: FC<FormContentProps> = ({
  countries,
  countriesLoading,
  kycData,
  submitLoading,
  revokeOtpVerificationLoading,
  mainInfo,
  lockClientIfPossible,
  unlockClient,
  onShowVersionHistory,
  onRevokeOtpVerification,
}) => {
  const { t } = useTranslation();
  const { resetForm, initialValues, setReadOnly } = useFormContext();

  const fullName = initialValues
    ? parseFullName(initialValues.personalData)
    : '';

  const headerTitle = fullName
    ? t('client.representativeClient.title', {
        name: fullName,
      })
    : '';

  const { onDisableReadOnly, onEnableReadOnly } = useLockAndUnlockReadOnly(
    setReadOnly,
    { unlockClient, lockClientIfPossible }
  );

  const {
    isOpen: isOpenRevokeOtp,
    onClose: onCloseRevokeOtp,
    open: openRevokeOtp,
  } = useDialog();

  const onActionRevokeOtpVerification = () => {
    openRevokeOtp();
  };

  const onConfirmRevokeOtp = async () => {
    await onRevokeOtpVerification?.().finally(() => onCloseRevokeOtp());
  };

  const actionMenuProps: ActionProps = {
    onRevokeOtpVerification: onRevokeOtpVerification
      ? onActionRevokeOtpVerification
      : undefined,
  };

  return (
    <>
      <ClientFormContent
        shownTabBar
        actionMenuProps={actionMenuProps}
        content={
          <RepresentativeSections
            countries={countries}
            countriesLoading={countriesLoading}
            kycData={kycData}
            mainInfo={mainInfo}
          />
        }
        headerTitle={headerTitle}
        resetForm={resetForm}
        submitLoading={submitLoading}
        onEdit={onDisableReadOnly}
        onEnableReadOnly={onEnableReadOnly}
        onShowVersionHistory={onShowVersionHistory}
      />
      <DialogConfirmRevokeOtpVerification
        fullName={fullName}
        isOpen={isOpenRevokeOtp}
        loading={revokeOtpVerificationLoading}
        onClose={onCloseRevokeOtp}
        onConfirm={onConfirmRevokeOtp}
      />
    </>
  );
};
